import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {Group, Stack, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import {
  AntibodySchemaDomainLinkedSchemasQuery,
  AntibodySchemaDomainLinkedSchemasQueryVariables,
} from 'antibodies/actions/__generated/AntibodySchemaDomainLinkedSchemasQuery';
import {ANTIBODY_SCHEMA_DOMAIN_LINKED_SCHEMAS_QUERY} from 'antibodies/actions/AntibodyActionUtil';
import {
  AntibodyDomainSearchInput,
  AntibodyChainSearchInput,
} from 'antibodies/actions/AntibodyComponentSearchInput';
import {AntibodyFormat, DistinctDomain} from 'antibodies/AntibodyTypedefs';
import {
  deprecatedDistinctDomainToKey,
  formatToSortedDistinctDomainsWithChainIndex,
} from 'antibodies/AntibodyUtils';
import Antibody from 'antibodies/visualization/Antibody';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export type DomainWithProtein = Pick<DistinctDomain, 'label' | 'type' | 'specificity'> & {
  proteinId: string | null;
};

export interface AntibodyComponentInputsProps {
  antibodySchemaId: string;
  disabled: boolean;
  format: AntibodyFormat;
  domainsWithProteinByLabel: Record<string, DomainWithProtein>;
  setDomainsWithProteinByLabel: (newDomainsWithProteinByLabel: Record<string, DomainWithProtein>) => void;
}

interface DomainLinkedSchemaSearchConfig {
  schemaOrInterfaceId: string | null;
  domainTypeFieldId: string | null;
  domainTypeOptionId: string | null;
}

/**
 * A set of ChipSearchInput that can be used to associate AA Sequence entities with antibody domains.
 * @param schemaId The API ID of the antibody schema
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param format The format of the antibody being created
 * @param domainsWithProteinByLabel A map domain labels to domains with their associated protein
 * @param setDomainsWithProteinByLabel A function to update domainsWithProteinByLabel
 */
export default function AntibodyComponentInputs({
  antibodySchemaId,
  disabled,
  format,
  domainsWithProteinByLabel,
  setDomainsWithProteinByLabel,
}: AntibodyComponentInputsProps): JSX.Element {
  const sortedDistinctDomains = formatToSortedDistinctDomainsWithChainIndex(format);
  const domainLinkedSchemasQueryResult = useBenchlingQuery<
    AntibodySchemaDomainLinkedSchemasQuery,
    AntibodySchemaDomainLinkedSchemasQueryVariables
  >(ANTIBODY_SCHEMA_DOMAIN_LINKED_SCHEMAS_QUERY, {variables: {schemaId: antibodySchemaId}});

  const linkedSchemasByTypeAndSpecificity = useMemo(() => {
    if (
      domainLinkedSchemasQueryResult.type !== QueryResultType.SUCCESS ||
      domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface == null ||
      isInaccessible(domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface)
    ) {
      return {};
    }
    const {domainLinkedSchemas} = domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface;
    return domainLinkedSchemas.reduce(
      (acc, spec) => {
        acc[deprecatedDistinctDomainToKey({type: spec.domainType, specificity: spec.specificity})] = {
          schemaOrInterfaceId: spec.schemaOrInterfaceId,
          domainTypeFieldId: spec.domainTypeFieldId,
          domainTypeOptionId: spec.domainTypeOptionId,
        };
        return acc;
      },
      {} as Record<string, DomainLinkedSchemaSearchConfig>
    );
  }, [domainLinkedSchemasQueryResult]);

  if (domainLinkedSchemasQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  const domainSchemasQueryError =
    domainLinkedSchemasQueryResult.type === QueryResultType.ERROR ||
    domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface == null ||
    isInaccessible(domainLinkedSchemasQueryResult.data.implementedAntibodySchemaInterface);

  return (
    <Group align="flex-start" spacing={16}>
      <Stack spacing={0} sx={{flex: 1}}>
        <Text size="12px" color={Color.GRAY_700} weight={FONT_WEIGHT_PROXIMA_SEMIBOLD} mb={8}>
          Preview
        </Text>
        <Stack p="16px" spacing={0} bg={Color.GRAY_50} mah="400px" sx={{borderRadius: '8px'}}>
          <Antibody format={format} showLabels={true} showPopovers={true} />
        </Stack>
      </Stack>
      <Stack spacing={0} sx={{flex: 1}}>
        {domainSchemasQueryError && (
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Failed to load protein domain configuration"
          />
        )}
        <>
          <Text size="12px" color={Color.GRAY_700} weight={FONT_WEIGHT_PROXIMA_SEMIBOLD} mb={8}>
            Chains
          </Text>
          <Stack spacing={0}>
            {format.chains.map((distinctChain, chainIndex) => (
              <AntibodyChainSearchInput
                key={distinctChain.label}
                distinctChain={distinctChain}
                chainIndex={chainIndex}
                disabled={true}
                selectedProtein={null}
                setSelectedProtein={noop}
                linkedSchemaId={null}
              />
            ))}
          </Stack>
        </>
        <Text size="12px" color={Color.GRAY_700} weight={FONT_WEIGHT_PROXIMA_SEMIBOLD} mb={8}>
          Domains
        </Text>
        <Stack spacing={0}>
          {sortedDistinctDomains.map((distinctDomain) => {
            const linkedSchemaConfig =
              linkedSchemasByTypeAndSpecificity[
                deprecatedDistinctDomainToKey({
                  type: distinctDomain.type,
                  specificity: distinctDomain.specificity,
                })
              ];
            return (
              <AntibodyDomainSearchInput
                key={distinctDomain.label}
                disabled={disabled || domainSchemasQueryError}
                distinctDomain={distinctDomain}
                selectedProtein={domainsWithProteinByLabel[distinctDomain.label]?.proteinId}
                setSelectedProtein={(newSelectedProtein: string | null) => {
                  setDomainsWithProteinByLabel({
                    ...domainsWithProteinByLabel,
                    [distinctDomain.label]: {
                      ...distinctDomain,
                      proteinId: newSelectedProtein,
                    },
                  });
                }}
                linkedSchemaId={linkedSchemaConfig?.schemaOrInterfaceId}
                domainTypeFieldId={linkedSchemaConfig?.domainTypeFieldId}
                domainTypeOptionId={linkedSchemaConfig?.domainTypeOptionId}
              />
            );
          })}
        </Stack>
      </Stack>
    </Group>
  );
}
